import { Injectable } from '@angular/core';
import { UtilService } from '@app/shared/services/utils/util.service';
import { BehaviorSubject, Observable, delay, filter, first, map, tap } from 'rxjs';
import { IWidget, WidgetViewStates } from '../constants/interfaces';
import { userWidgetsKey, autoRefreshKey } from '../constants/constants';
import { BrowserLocalStorageService } from '@app/shared/services/utils/browserLocalStorage.service';

@Injectable({
  providedIn: 'root',
})
export class WidgetsService {
  public addWidgetEvent$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  // view state types: widgets (1 or more widgets), no_widgets and loading
  public viewState$: BehaviorSubject<WidgetViewStates> = new BehaviorSubject(WidgetViewStates.LOADING);
  public itemChange$: BehaviorSubject<string> = new BehaviorSubject(null);
  public itemResize$: BehaviorSubject<string> = new BehaviorSubject(null);
  public refreshWidgets$: BehaviorSubject<boolean> = new BehaviorSubject(null);
  public disableAdd$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  public autoRefreshState$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  public manualRefresh$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  public manualRefreshConfig = {
    label: 'widgets.refresh-all',
    dataCy: 'refresh-all-widgets',
    onClick: () => this.manualRefresh$.next(true),
  };

  constructor(private _browserLocalStorageService: BrowserLocalStorageService) {
    this.refreshWidgets$.pipe(tap(() => this.refreshWidgets$.next(false)));
  }

  public loadWidgets(): Observable<IWidget[]> {
    return this._browserLocalStorageService.requestAPILocalStorageData().pipe(
      first(),
      map((data) => {
        const encriptedKey = UtilService.encryptData(userWidgetsKey);
        const item: Record<string, any> | undefined = data.find(
          (item) => item.key.toUpperCase() === encriptedKey.toUpperCase()
        );
        const userWidgets = item ? this._browserLocalStorageService.safeJsonParse(item.value) : null;
        return userWidgets?.length ? userWidgets : null;
      })
    );
  }

  public saveWidgets(widgets): Observable<any> {
    this.disableAdd$.next(true);
    return this._browserLocalStorageService.saveAPILocalStorage(userWidgetsKey, JSON.stringify(widgets)).pipe(
      tap(() => this.disableAdd$.next(false)),
      first()
    );
  }

  public loadAutoRefresh(): void {
    const autoRefresh = this._browserLocalStorageService.getAPILocalStorage(autoRefreshKey);
    const parsedAutoRefresh = autoRefresh ? autoRefresh : false;
    this.autoRefreshState$.next(parsedAutoRefresh);
  }

  public saveAutoRefresh(state): Observable<any> {
    return this._browserLocalStorageService.saveAPILocalStorage(autoRefreshKey, JSON.stringify(state)).pipe(first());
  }

  public toggleAutoRefresh(event): void {
    this.autoRefreshState$.next(event);
    this.saveAutoRefresh(event).subscribe();
  }
}
